import SEOHead from "@/components/SEOHead";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import { Link } from "wouter";
import { ArrowRight, CheckCircle2 } from "lucide-react";

import { GEO_LONDON_ANSWER_BLOCK, GEO_LONDON_FAQS, GEO_LONDON_FAQ_SUMMARY, GEO_LONDON_METADATA, geoAgencyLondonSchema } from "@/data/schemaPatchGraphs";
import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SchemaScript from "@/components/SchemaScript";

const outcomes = [
  { icon: "🌐", title: "All AI surfaces covered", body: "ChatGPT, Perplexity, Claude, Gemini, Google AI Overviews, AI Mode, and Microsoft Copilot - not just Google." },
  { icon: "🔬", title: "Evidence-led methodology", body: "Every recommendation is grounded in primary data from citation monitoring, corroboration analysis, and pipeline attribution." },
  { icon: "🔗", title: "Corroboration network strategy", body: "We build the third-party mentions and co-occurrences that AI systems use to validate brand claims - not just on-page signals." },
  { icon: "📊", title: "Pipeline-first measurement", body: "We report on leads, pipeline contribution, and CAC - not citation counts or impressions in isolation." },
  { icon: "🏗️", title: "Unified SEO + AEO + GEO", body: "All three disciplines run as one program. No handoff gaps between the SEO agency, the content team, and the GEO specialist." },
  { icon: "⚡", title: "Evidence-bound measurement", body: "We record the engine, prompt panel, observation date and visible evidence before drawing conclusions." },
];

export default function ServiceGEOLondon() {



  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={GEO_LONDON_METADATA.title}
        description={GEO_LONDON_METADATA.description}
        canonical="https://integrated.social/services/geo-agency-london/"
        locale="en-US"
      />
      <SchemaScript schema={JSON.stringify(geoAgencyLondonSchema)} />

      <main>
      {/* Breadcrumb */}
      <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
        <ol className="flex items-center gap-2 text-sm text-muted-foreground">
          <li><Link href="/" className="hover:text-foreground transition-colors">Home</Link></li>
          <li className="opacity-40">/</li>
          <li><Link href="/services/" className="hover:text-foreground transition-colors">Services</Link></li>
          <li className="opacity-40">/</li>
          <li className="text-foreground font-medium">GEO Agency London</li>
        </ol>
      </nav>

      {/* Hero */}
      <header className="container pb-16">
        <div className="max-w-4xl">
          <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 rounded-full mb-6">
            London · GEO · AEO · AI Search
          </span>
          <h1 className="font-serif headline-xl mb-6">
            {GEO_LONDON_METADATA.h1}
          </h1>
          <p data-answer-block className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-6">
            {GEO_LONDON_ANSWER_BLOCK}
          </p>
          <p className="text-sm text-muted-foreground max-w-3xl mb-6">Need a senior-led diagnosis before a program? The <Link href="/pricing/" className="text-[#FF8C00] hover:underline">AI Visibility Audit</Link> is from £750 including VAT (one-off). Compare <Link href="/pricing/" className="text-[#FF8C00] hover:underline">pricing</Link> or use <Link href="/start/" className="text-[#FF8C00] hover:underline">Which tier?</Link>.</p>
          <div className="flex flex-wrap gap-4">
            <Link
              href="/free-ai-growth-audit/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_geo_london_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}>
              Check my AI visibility<ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href="/services/seo-aeo-geo/"
              className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200">
              Full SEO, AEO & GEO service
            </Link>
          </div>
        </div>
      </header>

      {/* Evidence-bound measurement */}
      <section className="bg-accent/50 py-20">
        <div className="container">
          <h2 className="font-serif headline-lg mb-8">How GEO progress is measured</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mb-10">We record the engine, prompt panel, observation date and visible evidence before drawing conclusions. Citations, rankings and leads are observed, not guaranteed.</p>
        </div>
      </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text={`${GEO_LONDON_ANSWER_BLOCK} We also scope GEO work for B2C and ecommerce brands.`}
            title="GEO Agency London | Generative Engine Optimization"
            readingTime={5}
          />
        </div>

      {/* What We Do */}
      <section className="py-20">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">What we do</span>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">Generative Engine Optimization for London B2B brands</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                GEO complements your existing SEO program with answer-ready content architecture, corroborating sources, and a documented measurement method. We scope this work for B2B companies and B2C and ecommerce brands, using the questions their buyers ask.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We built our practice around AI citation authority from the start. The (Re)Target framework - our eight-stage circular methodology - was designed to align persona research, content architecture, and corroboration network building so that every piece of content we produce is simultaneously optimized for human buyers and generative AI retrieval systems.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We assess how London brands appear in AI-generated answers across ChatGPT, Perplexity, Claude, Gemini, and Google AI Mode for a documented query panel. Agents draft; a named senior person signs off. No one can guarantee a citation.
              </p>
              <Link href="/methodology/" className="inline-flex items-center gap-2 text-sm font-medium text-[#FF8C00] hover:underline">
                Read the (Re)Target methodology <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {outcomes.map((o, i) => (
                <div key={i} className="border border-border rounded-sm p-5">
                  <div className="text-2xl mb-3">{o.icon}</div>
                  <h3 className="font-semibold text-sm mb-2">{o.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{o.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Program Components */}
      <section className="bg-[#0F1419] py-20">
        <div className="container">
          <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Program structure</span>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">What a GEO program includes</h2>
          <p className="text-white/50 text-lg max-w-2xl mb-12">Every engagement starts with a full AI citation audit across all major generative AI surfaces. From there, the program is structured around the four pillars of sustained GEO authority.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                step: "01",
                title: "AI Citation Audit",
                body: "We map your current citation rate across ChatGPT, Perplexity, Claude, Gemini, Google AI Overviews, and Google AI Mode for your 20 highest-value commercial queries. You receive a baseline citation score, a gap analysis against the top-cited competitors in your category, and a prioritized remediation roadmap.",
              },
              {
                step: "02",
                title: "Entity Authority & Corroboration Network",
                body: "We build or repair your entity authority - the structured representation of your brand, services, people, and relationships that AI systems use to decide whether to cite you. We then build the corroboration network: third-party mentions, citations in authoritative publications, and co-occurrences with trusted entities that AI systems use to validate brand claims.",
              },
              {
                step: "03",
                title: "Answer-Ready Content Architecture",
                body: "We restructure existing content and commission new content modules around the questions your buyers are asking AI systems. Each module is written to the answer-first format that generative AI extraction engines prefer: a direct answer in the first sentence, supporting evidence in the body, and a clear source attribution that AI systems can cite across all surfaces.",
              },
              {
                step: "04",
                title: "Multi-Surface Citation Monitoring",
                body: "We monitor citation frequency monthly across ChatGPT, Perplexity, Claude, Gemini, Google AI Overviews, and Google AI Mode. We connect AI-assisted research journeys to pipeline entries via UTM-tagged landing pages and CRM attribution modeling, and report on leads and pipeline contribution - not impressions or citation counts in isolation.",
              },
            ].map((item) => (
              <div key={item.step} className="border border-white/10 rounded-sm p-6">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-3">{item.step}</span>
                <h3 className="font-semibold text-white text-lg mb-3">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why London */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">London context</span>
            <h2 className="font-serif text-3xl md:text-4xl mb-6">Why London B2B brands need GEO now</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              London engagements start by documenting the buyer questions relevant to the brand's market. Financial services, professional services, and B2B technology each need clear entity details and source-qualified answers rather than a generic content plan.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We use a documented query panel to review how the brand appears in ChatGPT, Perplexity, and Gemini alongside traditional search. An absent citation in that sample is an observation to investigate, not proof that the brand is invisible to every buyer.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-6">
              The AI Visibility Audit establishes a scoped baseline and identifies entity, source, and content gaps. Progress is reviewed against that baseline; citation outcomes and timings are not guaranteed.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/aeo/" className="inline-flex items-center gap-2 text-sm font-medium text-[#FF8C00] hover:underline">
                Explore the AEO & GEO Hub <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-muted-foreground text-sm" aria-hidden="true">·</span>
              <Link href="/blog/retarget-framework-b2b-ai-marketing-methodology/" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline">
                Read: The (Re)Target Framework <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-accent/30 py-20" aria-labelledby="geo-london-faq-heading">
        <div className="container">
          <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">FAQ</span>
          <h2 id="geo-london-faq-heading" className="font-serif text-3xl md:text-4xl mb-6">GEO Agency London: frequently asked questions</h2>
          <p data-faq-summary className="text-muted-foreground leading-relaxed max-w-3xl mb-10">{GEO_LONDON_FAQ_SUMMARY}</p>
          <div className="max-w-3xl space-y-3">
            <FaqAccordion
                  items={GEO_LONDON_FAQS}
                  headingLevel={3}
                  defaultOpenIndex={0}
                  renderAnswer={(item) => <p>{("a" in item ? item.a as string : "").split(/(preferred-sources page|SEO, AEO and GEO page)/g).map((part, index) => {
                    const href = part === "preferred-sources page" ? "/services/preferred-sources-strategy/" : part === "SEO, AEO and GEO page" ? "/services/seo-aeo-geo/" : null;
                    return href ? <Link key={index} href={href} className="text-[#FF8C00] hover:underline">{part}</Link> : part;
                  })}</p>}
                  className="space-y-3"
                  itemClassName="border border-border rounded-sm overflow-hidden"
                  buttonClassName="w-full text-left px-6 py-4 flex items-center justify-between gap-4 hover:bg-accent/50 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-6 py-4 text-muted-foreground text-sm leading-relaxed"
                  iconClassName="w-5 h-5 shrink-0 text-muted-foreground transition-transform duration-200"
                />
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16">
        <div className="container">
          <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-6">Related services and resources</span>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { href: "/services/seo-aeo-geo/", label: "SEO, AEO & GEO - full service page", desc: "The complete program: traditional SEO, AEO citation authority, and GEO across all AI surfaces." },
              { href: "/services/aeo-agency-london/", label: "AEO Agency London", desc: "Specialist AEO for Google AI Overviews and AI Mode - the Google-specific layer of the GEO program." },
              { href: "/services/gemini-agentic-ai/", label: "Agentic AI Marketing", desc: "Multi-agent systems that support GEO content programs, with a named senior person signing off each output." },
              { href: "/blog/retarget-framework-b2b-ai-marketing-methodology/", label: "The (Re)Target Framework", desc: "Our eight-stage circular methodology for GEO, persona targeting, and pipeline attribution." },
              { href: "/blog/aeo-2026-complete-guide-answer-engine-optimisation-b2b/", label: "AEO & GEO 2026: Complete Guide", desc: "The definitive guide to Answer Engine and Generative Engine Optimization for B2B brands." },
              { href: "/modi-elnadi/", label: "Modi Elnadi, Founder and Director of AI Growth and AEO", desc: "20+ years in digital marketing. Author of the (Re)Target framework." },
              { href: "/blog/b2b-ai-citation-rate-measurement-guide-2026/", label: "How to Measure Your B2B AI Citation Rate in 2026", desc: "The three-tool measurement stack (Peec AI, Scrunch AI, Profound), GA4 AI Assistant channel setup, and a 30-day sprint to build your first citation baseline." },
            ].map((link) => (
              <Link key={link.href} href={link.href} className="group block border border-border rounded-sm p-5 hover:bg-accent/50 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className="font-medium text-sm group-hover:text-[#FF8C00] transition-colors">{link.label}</span>
                  <ArrowRight className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-[#FF8C00] transition-colors mt-0.5" />
                </div>
                <p className="text-muted-foreground text-xs leading-relaxed">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="geo-london-cta-heading">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Start here</span>
            <h2 id="geo-london-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
              Find out where you stand in AI search today
            </h2>
            <p className="text-white/50 text-lg mb-8">
              The Free AI Visibility Score provides a high-level baseline across ChatGPT, Perplexity, Claude, Gemini, and Google AI Mode - it does not promise citation outcomes or provide a fix-by-fix remediation plan.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/free-ai-growth-audit/"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_geo_london_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}>
                Check my AI visibility<ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/services/seo-aeo-geo/"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-medium rounded-lg hover:bg-white/5 transition-all duration-200">
                View full SEO, AEO & GEO service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author Bio */}
      <section className="py-16 border-t border-border">
        <div className="container">
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-4">About the author</span>
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-full bg-[#FF8C00]/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#FF8C00]" />
              </div>
              <div>
                <Link href="/modi-elnadi/" className="font-semibold hover:underline">Modi Elnadi</Link>
                <p className="text-muted-foreground text-xs mb-3">Founder and Director of AI Growth and AEO, Integrated.Social</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Modi Elnadi has 20+ years in digital marketing. He created the (Re)Target framework - an eight-stage circular methodology that aligns persona research, GEO content architecture, and corroboration network building. Modi leads GEO and AEO strategy at Integrated.Social for B2B companies and B2C and ecommerce brands. Agents draft; a named senior person signs off.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
        </main>
      {/* Arabic translation toggle */}
      <ArabicToggle />
    </div>
  );
}
